/* eslint-disable @typescript-eslint/no-explicit-any */
export enum StatFlags {
  None = 0,

  // NOTE: userId will be removed from payload,
  // anonymousId will be replaced with a new random one
  Anonymous = 1 << 0,

  // NOTE: both session flags are ignored by _trackBatch_
  OnceInSession = 1 << 1,
  OnlyFirstSession = 1 << 2,

  // NOTE: event won't be sent if worker is not available
  OnlyForWorker = 1 << 3,

  // NOTE: send event immediately without pushing it to a batch queue
  SendImmediately = 1 << 4,

  // NOTE: don't add common params to the event/events
  NoCommonParams = 1 << 5,
}

export type BufferItem = {
  name: string
  type: 'Uint32Array' | 'Float32Array' | 'Int32Array'
  buffer: ArrayBuffer
  bufferLength: number
}

export type BufferDataParams = {
  // NOTE: this is a low level API, use it only when you understand what you are doing.
  // This is mostly needed to speedup the transfer of the data to the Worker. All that items
  // will be recreated inside worker according to passed _type_ field and they will be inserted to
  // event properties with a passed _name_ key. _bufferLength_ is needed to truncate the buffer on
  // the worker side to avoid doing this in calling thread.
  __bufferData?: {
    // NOTE: if passed buffers are also declared here in the list as transferables, then
    // they will be moved to worker completely:
    // https://developer.mozilla.org/ru/docs/Web/API/Worker/postMessage
    transferables?: ArrayBuffer[] // for now only ArrayBuffer according to BufferItem
    items: BufferItem[]
  }
}

export type StatEvent = {
  event: string
  timestamp?: Date
  properties?: {
    [x: string]: any
  } & BufferDataParams
}

export type PageEvent = {
  timestamp?: Date
  properties?: {
    title?: string
    path?: string
  }
}

export type UserIds = {
  anonymousId: string
  userId: string | undefined
}

export type StatWorker = {
  postMessage(payload: WorkerData): void
}

export type StatAppAPI = {
  getStoredUserIds(): Partial<UserIds>
  storeUserIds(userIds: UserIds): void
  addCommonStatParams(params: Record<string, unknown>): Record<string, unknown>
  getStatContextPage(statHandler: StatHandler): StatContextPage
  getUserAgent(): string

  sendError(error: any, extra: any): void
}

export type StatBatch = {
  tracks: TrackParams[]
  flags: StatFlags
  flushAt: number
}

export type StatHandler = UserIds & {
  __statId: number
  __lastUserSource: string | undefined
  apiHost: string
  library: string
  version: string

  app: StatAppAPI

  isFirstSession: boolean
  onceInSessionSentEvents: Set<string>

  batches:
    | {
        buckets: Map<string, Map<number, StatBatch>>
        flushAt: number
      }
    | undefined
  flushBatchesIntervalId: number | undefined

  worker: StatWorker | undefined
}

export type StatOptions = {
  apiHost: string
  library: string
  version: string

  batches?: {
    flushAt: number
  }

  app: StatAppAPI
}

export type StatContextPage = {
  referrer: string
  path: string
  title: string
  url: string
  search: string
  site_language: string
  inIframe: boolean
}

export type StatContext = {
  library: {
    name: string
    version: string
  }
  page: StatContextPage
  userAgent: string
}

export type BaseStatParams = {
  apiKey: string
  apiHost: string
  context: StatContext
}

export type UserInfo = {id: string; name: string; email: string}

export type IdentifyParams = BaseStatParams &
  UserIds & {
    user: UserInfo
    timestamp?: Date
  }

export type TrackParams = BaseStatParams & UserIds & StatEvent

export type PageParams = BaseStatParams & UserIds & PageEvent

export type BatchParams = {
  apiKey: string
  apiHost: string
  tracks: TrackParams[]
}

export type StatsBasicMethods = {
  identify(params: IdentifyParams): void
  track(params: TrackParams): void
  trackBatch(params: BatchParams): void
  page(params: PageParams): void
}

export type StatsApiType = StatsBasicMethods & {
  UUID(): string
}

/*
 * Worker section
 */

export type WorkerIdentifyData = {
  event: 'identify'
  payload: IdentifyParams
}

export type WorkerTrackData = {
  event: 'track'
  payload: TrackParams
}

export type WorkerTrackBatchData = {
  event: 'trackBatch'
  payload: BatchParams
}

export type WorkerPageData = {
  event: 'page'
  payload: PageParams
}

export type WorkerData =
  | WorkerIdentifyData
  | WorkerTrackData
  | WorkerTrackBatchData
  | WorkerPageData

/*
 * Tests section
 */

export type InternalStatsApiType = StatsApiType & {
  __processWorkerMessage(
    data: WorkerData,
  ): {event: 'initialized'; payload: {topHandler: any; workerHandler: number}} | undefined
  __stringToBase64(input: string): string
  __METHODS_CONTAINER: StatsBasicMethods
}
